import { useEffect, useCallback, useRef } from "react";
import { driver, DriveStep, Driver } from "driver.js";
import "driver.js/dist/driver.css";

export const useMealPlanActivatedTour = (
  shouldStart: boolean,
  onComplete: () => void,
  krogerConnected: boolean
) => {
  const driverRef = useRef<Driver | null>(null);

  const startTour = useCallback(() => {
    // Touch-primary devices tap; mouse/trackpad devices click
    const tap = window.matchMedia('(pointer: coarse)').matches ? 'Tap' : 'Click';
    const steps: DriveStep[] = [
      {
        element: '[data-tour="grocery-list-intro"]',
        popover: {
          title: "Your grocery list is ready!",
          description:
            `Your shopping list has been created with all the ingredients you need. ${tap} it any time to open the list, check off items you already have, and add items to your pantry.`,
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="add-item-button"]',
        popover: {
          title: krogerConnected
            ? "Add other items & customize shopping"
            : "Add other items",
          description: krogerConnected
            ? `Need something extra? ${tap} here to add other items to your list, such as household essentials or snacks, or customize product preferences for your Kroger order.`
            : `Need something extra? ${tap} here to add other items to your shopping list, such as household essentials or snacks.`,
          side: "top",
          align: "start",
        },
      },
    ];

    // Add cart/instacart step
    steps.push({
      element: krogerConnected
        ? '[data-tour="add-to-kroger-cart"]'
        : '[data-tour="create-instacart-list"]',
      popover: {
        title: krogerConnected ? "Add to Kroger cart" : "Shop with Instacart",
        description: krogerConnected
          ? `${tap} to add items to your Kroger cart for pickup or delivery. We'll match products and add them automatically!`
          : `${tap} to send your list to Instacart where you can then review and purchase your items.`,
        side: "top",
        align: "start",
      },
    });

    const driverObj = driver({
      showProgress: true,
      steps,
      onDestroyStarted: () => {
        driverObj.destroy();
        driverRef.current = null;
        onComplete();
      },
      popoverClass: "meal-plan-tour-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Got it!",
      progressText: "{{current}} of {{total}}",
    });

    driverRef.current = driverObj;
    driverObj.drive();
  }, [onComplete, krogerConnected]);

  useEffect(() => {
    if (shouldStart) {
      // Delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        startTour();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldStart, startTour]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
        driverRef.current = null;
      }
    };
  }, []);

  return { startTour };
};
